import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

import PageHeader from "components/Common/PageHeader";
import TableFilterDynamique from "components/Common/TableFilter/TableFilterDynamique";
import CustomButton from "components/Common/ui/CustomButton";

import { all_routes } from "router/all_routes";
import { FilterField } from "components/Common/TableFilter/TableFilter";
import { useAbility } from "config/abilityContext";
import useCashBox from "../hooks/useCashBox";
import CashBoxesTable from "../table/CashBoxesTable";
import AddEditCashBoxModal from "../modals/AddEditCashBoxModal";
import DetailCashboxTransactionModal from "../modals/DetailCashboxTransactionModal";
import { formatToSQLDate } from "utils/dateUtils";


const CashBoxesPage = () => {
    const { t } = useTranslation();
    const ability = useAbility();

    const {
        cashBoxes,
        loading,
        users,
        infoPagination,
        fetchCashBoxes,
        handleClose,
        handleDelete,
        fetchUsers,
        handleCreate
    } = useCashBox();

    const today = new Date();
    const startOfDay = new Date(today);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);

    const [filters, setFilters] = useState<{ [key: string]: any }>({ startDay: formatToSQLDate(startOfDay), endDay: formatToSQLDate(startOfDay) });
    const [isModalOpen, setIsModalOpen] = useState(false);

    const breadcrumbItems = [
        { label: "finance_dashboard", path: all_routes.financeDashboard },
        { label: t("cashbox.title"), active: true },
    ];
    const [isModalDetailOpen, setIsModalDetailOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);

    const handleViewDetails = (transaction: any) => {
        setSelectedTransaction(transaction);
        setIsModalDetailOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTransaction(null);
    };
    useEffect(() => {
        fetchCashBoxes({ page: 1, limit: 10 , ...filters });
        fetchUsers();
    }, [fetchCashBoxes, fetchUsers]);

    const filterFields: FilterField[] = [
        {
            key: "status",
            label: t("cashbox.filters.status"),
            type: "select",
            options: [
                { label: t("cashbox.status.open"), value: "open" },
                { label: t("cashbox.status.closed"), value: "closed" },
            ],
        },   
        { key: "userId", label: t("cashbox.fields.user"), type: "select", options: users.map((user: any) => ({ label: user.firstName + " " + user.lastName, value: user.id })) },
        { key: "date", label: t("cashbox.fields.day"), type: "range", rangeKeys: { start: "startDay", end: "endDay" } },
    ];

    const handleFilter = (newFilters: any) => {
        const params = {
            ...newFilters,
            page: 1,
            limit: 10,
        };

        setFilters(newFilters);
        fetchCashBoxes(params);
    };

    const handleSaveCashBox = async (data: any) => {
        const result = await handleCreate(data);
        if (result) {
            setIsModalOpen(false);
            fetchCashBoxes({ page: 1, limit: 10 , ...filters });
        }
        return result;
    };


    return (
        <div className="page-wrapper">
            <div className="content container-fluid">

                <PageHeader title={t("cashbox.title")} breadcrumbItems={breadcrumbItems} />

                <div className="d-flex mb-3 justify-content-end gap-2">
                    {ability.can("create", "CashBox") && (
                        <CustomButton onClick={() => setIsModalOpen(true)}>
                            <i className="ti ti-plus me-2" />
                            {t("cashbox.actions.open_today")}
                        </CustomButton>
                    )}

                    <TableFilterDynamique
                        fields={filterFields}
                        filters={filters}
                        onApply={handleFilter}
                        onReset={() => {
                            setFilters({});
                            fetchCashBoxes({ page: 1, limit: 10 });
                        }}
                    />
                </div>

                <CashBoxesTable
                    data={cashBoxes}
                    loading={loading}
                    onClose={handleClose}
                    onDelete={handleDelete}
                    dataPaginate={{
                        current: infoPagination?.page || 1,
                        pageSize: infoPagination?.limit || 10,
                        total: infoPagination?.total,
                    }}
                    onChange={(pg) => {
                        const params = {
                            ...filters,
                            page: pg.current || 1,
                            limit: pg.pageSize || 10,
                        };

                        fetchCashBoxes(params);
                    }}
                    handleViewDetails={handleViewDetails}
                />

                <AddEditCashBoxModal
                    users={users}
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveCashBox}
                />

                <DetailCashboxTransactionModal
                    isOpen={isModalDetailOpen}
                    onClose={handleCloseModal}
                    transaction={selectedTransaction}

                />
            </div>
        </div>
    );
};

export default CashBoxesPage;
